/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { ITaskFollowupBinding } from 'app/core/components/tasks/task';
import { CaseService } from 'app/cm/services/case-service';
import { safe } from 'app/shared/lib/utilities';
import { apiCall } from 'app/shared/lib/api/api';
import { TaskWithLocalState } from 'app/core/models/local-state-extensions';
import { ApiDate } from 'app/shared/models/essette-api';
import { QueueService } from 'app/core/services/queue-service';
import { TaskService } from 'app/core/services/task-service';
import { getReference } from 'app/shared/models/essette-types';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { Toaster } from 'app/shared/lib/toaster';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { EssCollapseCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { guid } from 'app/shared/lib/guid';
import { bindable } from 'aurelia-typed-observable-plugin';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class TaskFollowups extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    private goalId: string | null = null;
    private definitionId: string | null = null;
    private tasksConfig: ITasksConfig | null = null;

    protected suggestedFollowups: TaskWithLocalState[] = [];
    protected requiredFollowups: TaskWithLocalState[] = [];
    protected queues: ReadonlyArray<Core.Queue> = [];
    protected queueProviders: ReadonlyArray<CD.Provider> = [];

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public taskType: string | null = null;

    /** Used to show copy all on delegated to user if all queues are the same or blank */
    protected allQueuesBlankOrSame: boolean = false;

    protected loaded: Promise<void>;
    protected load!: () => void;

    private sortingRequired: Sorting = new Sorting();
    private sortingSuggested: Sorting = new Sorting();

    protected id: guid = guid();


    ////****************** Element References ***************************************////
    protected $collapsible!: EssCollapseCustomAttribute

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly caseService: CaseService,
        private readonly queueService: QueueService,
        private readonly taskService: TaskService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);

        this.loaded = new Promise<void>((resolve) => this.load = resolve);
    }

    protected async activate(binding: ITaskFollowupBinding): Promise<void> {
        if(!binding) { throw new Error('Missing required binding'); }

        this.goalId = binding.targetId;
        this.definitionId = binding.taskDefinitionId;
        this.tasksConfig = binding.tasksConfig;

        this.taskService.configure(this.tasksConfig);

        this.loadLookups();
        await this.reload();
        await this.insertRequiredFollowups(); // This inserts the follow up intervention, and is necessary since the modal is just for editing.

        this.loaded.then(value => {
            if(this.suggestedFollowups.length > 0) {
                this.$collapsible.open(1);
            } else {
                this.$collapsible.open(0);
            }
        });
    }

    protected attached(): void {
        this.load();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySortSuggested(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sortingSuggested } = this;
        if(direction === null) {
            sortingSuggested.remove(property);
            if(sortingSuggested.entries.length === 0) {
                sortingSuggested.add({
                    reference: property,
                    direction: SortDirection.Ascending,
                });
            }
        } else {
            sortingSuggested.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(this.suggestedFollowups, sortingSuggested);
    }

    protected applySortRequired(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sortingRequired } = this;
        if(direction === null) {
            sortingRequired.remove(property);
            if(sortingRequired.entries.length === 0) {
                sortingRequired.add({
                    reference: property,
                    direction: SortDirection.Ascending,
                });
            }
        } else {
            sortingRequired.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(this.requiredFollowups, sortingRequired);
    }

    protected delegateToChanged(changedTask: Core.Task | null): void {
        if(!(changedTask && changedTask.delegated && changedTask.delegated.queue)) { return; }

        const queue = safe(() => changedTask.delegated!.queue) || null;
        if(!(queue && queue.delegated && queue.delegated.type)) { return; }

        if(changedTask.delegated && changedTask.delegated.to) {
            changedTask.delegated.to.type = {
                id: queue.delegated.type.id,
                code: queue.delegated.type.code
            };
        }
    }

    protected async delegateQueueChanged(followups: ReadonlyArray<TaskWithLocalState>, changedTask: Core.Task | null): Promise<void> {
        if(changedTask && changedTask.delegated) { changedTask.delegated.to = null; }

        if(followups.length === 0) { this.allQueuesBlankOrSame = true; }

        const firstSelectedQueue = safe(() => followups[0].delegated!.queue) || null;
        if(firstSelectedQueue) {
            this.allQueuesBlankOrSame = followups.every(t => {
                const queueId = safe(() => t.delegated!.queue!.id)
                return queueId === firstSelectedQueue.id || !queueId;
            });
        }
        else {
            this.allQueuesBlankOrSame = false;
        }

        // Ensure providers are loaded for a task with the Portal Provider queue selected
        const queueType = changedTask?.delegated?.queue?.delegated?.type?.code;
        if(queueType === 'CD.Provider' && this.queueProviders.length === 0) {
            this.queueProviders = await this.loadProviders();
        }
    }


    ////****************** Functions ************************************************////
    private async reload(): Promise<void> {
        if(this.goalId && this.definitionId) {
            const [tasks, suggested, required] = await Promise.all([
                this.loadTasks(this.goalId),
                this.loadFollowups(this.definitionId, true, this.sortingSuggested),
                this.loadFollowups(this.definitionId, false, this.sortingRequired)
            ]);

            this.suggestedFollowups = this.filterFollowups(tasks || [], suggested || []);
            this.requiredFollowups = this.filterFollowups(tasks || [], required || []);
        }
    }

    private filterFollowups(
        existingTasks: ReadonlyArray<CM.Task> = [],
        followups: ReadonlyArray<CM.TaskDefinitionWithFollowupDetailModel> = []): TaskWithLocalState[] {

        // Get array of intervention definition ids from existing tasks
        const definitionIds = existingTasks.map(i => {
            return safe(() => { return i.definition!.id; }) || '';
        });

        return followups
            // If a followup definition id already exists, filter it out
            .filter((t) => {
                return definitionIds.findIndex((i) => i === t.id) === -1;
            })
            // Map into a task object
            .map((t) => {
                const optional = t.detail && !!t.detail.isOptional;
                const task: TaskWithLocalState = {
                    isCustom: false,
                    definition: getReference(t),
                    description: t.description,
                    delegated: {
                        queue: getReference(t.defaultQueue)
                    },
                    _state: { isLocked: !optional }
                };
                return task;
            });
    }

    protected copyDelegatedQueue(followups: ReadonlyArray<TaskWithLocalState>, queue: Core.Queue | null): void {
        followups.forEach(t => {
            if(!queue) {
                delete t.delegated;
                return;
            }

            if(!t.delegated) {
                t.delegated = {};
            }
            t.delegated.queue = queue;
        });

        this.allQueuesBlankOrSame = true;
    }
    protected copyDelegatedTo(followups: ReadonlyArray<TaskWithLocalState>, delegateTo: Core.TaskDelegateTargetModel | null): void {
        followups.forEach(t => {
            if(!delegateTo && t.delegated) {
                delete t.delegated.to;
                return;
            }

            if(!t.delegated) {
                t.delegated = {};
            }
            t.delegated.to = delegateTo;
        });
    }
    protected copyDueDate(followups: ReadonlyArray<TaskWithLocalState>, date: ApiDate | null): void {
        followups.forEach(t => {
            if(!date) {
                delete t.due;
                return;
            }

            if(!t.due) {
                t.due = {};
            }
            t.due.date = date;
        });
    }

    /** Takes a task from the followups grid and constructs a new copy to be sent to the API */
    protected buildSaveObject(task: TaskWithLocalState): TaskWithLocalState {
        if(!task._state) { task._state = {}; }

        const saveTask: TaskWithLocalState = {
            due: !!task.due && !!task.due.date ? { date: task.due.date } : undefined,
            id: task.id
        };

        if(task.isCustom) {
            saveTask.isCustom = true;
            saveTask.description = task.description;
        } else {
            saveTask.definition = getReference(task.definition);
        }

        if(task.delegated && task.delegated.queue) {
            saveTask.delegated = {
                queue: getReference(task.delegated.queue),
                to: task.delegated.to && task.delegated.queue && task.delegated.queue.delegated ? {
                    id: task.delegated.to.id,
                    type: task.delegated.queue.delegated.type
                } : undefined
            }
        }

        if(this.taskService.config.addTargetProperty) { this.taskService.config.addTargetProperty(saveTask) };

        return saveTask;
    }

    private async insertRequiredFollowups(): Promise<boolean> {
        return await this.saveTasks(this.requiredFollowups);
    }


    ////****************** IForm ****************************************************////
    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        return await this.saveTasks([...this.requiredFollowups, ...this.suggestedFollowups]);
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadTasks(goalId: string): Promise<CM.Task[]> {
        try {
            this.state.beginLoad();
            const response = await this.taskService.loadTasksByTarget(goalId, { all: true }, [], false);
            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadFollowups(definitionId: string, isOptional: boolean, sorting: Sorting): Promise<CM.TaskDefinitionWithFollowupDetailModel[]> {
        const options: Partial<IQueryLoadOptions> = {
            sorting: sorting,
            all: true
        };

        try {
            this.state.beginLoad();

            let response;

            if(this.tasksConfig?.primaryTargetType === 'CM.CarePlanIntervention') {
                response = await this.caseService.loadInterventionFollowupsByIntervention(definitionId, isOptional, options);
            }
            else {
                response = await this.caseService.loadTaskFollowupsByTask(definitionId, isOptional, options);
            }
            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();
            const queues = await this.queueService.loadQueues(null, { all: true }, ['users', 'type']);
            this.queues = queues.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProviders(): Promise<Array<CD.Provider>> {
        try {
            this.state.beginLoad();
            let options = { all: true };
            const memberId = this.tasksConfig?.memberId || '';
            const response = await this.queueService.loadPortalProviders(memberId, options, [])
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall async saveTasks(sourceTasks: TaskWithLocalState[]): Promise<boolean> {
        const tasks = sourceTasks
            .map((i) => {
                const task = this.buildSaveObject(i);
                return task;
            });

        try {
            this.state.beginSave();

            const promises = new Array<Promise<IApiActionResults>>();
            tasks.forEach((i) => {
                if(!!i.id) {
                    promises.push(this.taskService.updateTask(i.id, i));
                } else {
                    promises.push(this.taskService.insertTask(i));
                }
            });

            const responses = await Promise.all(promises);
            for(let index = 0; index < responses.length; index++) {
                sourceTasks[index].id = responses[index].id;
            }

            if(responses.every((r) => r.isSuccessful)) {
                return Promise.resolve(true);
            }

            return Promise.resolve(false);

        } catch(error) {
            this.toaster.standard.saved('cm:carePlanIntervention.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
